import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationSettings,
} from '@/apiManager/notifications'
import { DEFAULT_COMMUNITY_TIMEZONE } from '@/components/Notifications/notificationConfig'

// In-app only: no polling. The badge refreshes on load, navigation (throttled),
// tab focus and when the bell opens.
const UNREAD_THROTTLE_MS = 30000
const LATEST_LIMIT = 10

// After an approve / reject the server recounts a request notification on a
// queue: ~5s later, or ~70s for a request still in its 60s batching buffer.
// Until then a fetch returns the old count, so the count the admin left is
// kept over it for a while, and the server is asked again once each delay
// has passed.
const LOCAL_COUNT_MS = 90000
const RECHECK_MS = [6000, 75000]
// The most rows the list API returns at once.
const PENDING_LIMIT = 50
let recheckTimers = []

const clearRechecks = () => {
  recheckTimers.forEach(clearTimeout)
  recheckTimers = []
}

// `entity` is what a requests page knows it by (a group's id, a challenge's
// slug); a community's member requests need none, the list is that community's.
const sameRequest = (item, { type, entity }) => item.type === type
  && Object.entries(entity).every(([key, value]) => String(item.entity?.[key]) === String(value))

// A row loaded after a requests page handled people: clear only if it shows
// everyone in it (3 people at most) and every one of them was handled, as the
// list does not say who else is still waiting.
const allHandled = (item, handled, now) => item.actionStatus === 'pending'
  && item.actors?.length
  && item.count <= item.actors.length
  && item.actors.every(actor => handled.some(done => done.until >= now
    && sameRequest(item, done)
    && done.userIds.includes(String(actor.id))))

// A server row still showing more pending people than the admin left keeps
// the local count; once it has caught up, its own value wins.
const withLocalCounts = (items, localCounts, handled = []) => {
  const now = Date.now()
  return items.map(item => {
    const local = localCounts[item.id]
    let pendingCount = null
    if (local && local.until >= now) pendingCount = local.pendingCount
    else if (allHandled(item, handled, now)) pendingCount = 0
    if (pendingCount === null || !(item.pendingCount > pendingCount)) return item
    return {
      ...item,
      pendingCount,
      actionStatus: pendingCount > 0 ? item.actionStatus : 'resolved',
    }
  })
}

// How many people each pending row loses: everyone handled counts once, in
// the newest row of that request showing them (the list is newest first).
const handledPerRow = (items, handled) => {
  const counts = new Map()
  handled.userIds.forEach(userId => {
    const row = items.find(item => sameRequest(item, handled)
      && item.actionStatus === 'pending'
      && item.actors?.some(actor => String(actor.id) === userId))
    if (row) counts.set(row, (counts.get(row) || 0) + 1)
  })
  return counts
}

const emptyUnread = () => ({
  paused: false,
  // The community's timezone: every time in the notifications is shown in it.
  timezone: null,
  total: 0,
  actionRequired: 0,
  byCategory: {},
  actionByCategory: {},
})

// Shared by every caller, so parallel triggers make one request.
let unreadRequest = null

export default {
  namespaced: true,
  state: {
    unread: emptyUnread(),
    unreadFetchedAt: 0,
    // The bell dropdown's list; the full page keeps its own.
    latest: [],
    latestLoaded: false,
    latestLoading: false,
    latestError: false,
    // The tab the bell last loaded, so a recheck reloads the same one.
    latestStatus: 'all',
    // { [notificationId]: { pendingCount, until } }, see LOCAL_COUNT_MS.
    localCounts: {},
    // [{ type, entity, userIds, until }]: people handled on a requests page,
    // for rows loaded only afterwards; kept as long as a local count.
    handled: [],
    // When the server was last asked again after an approve / reject, so the
    // notifications page can refresh its own list too.
    recheckedAt: 0,
  },
  getters: {
    badge: state => (state.unread.total > 99 ? '99+' : String(state.unread.total || '')),
    hasPendingAction: state => state.unread.actionRequired > 0,
    isPaused: state => !!state.unread.paused,
    timezone: state => state.unread.timezone || DEFAULT_COMMUNITY_TIMEZONE,
    // For lists kept outside the store (the notifications page).
    withLocalCounts: state => items => withLocalCounts(items, state.localCounts, state.handled),
  },
  mutations: {
    setUnread(state, unread) {
      state.unread = { ...emptyUnread(), timezone: state.unread.timezone, ...unread }
      state.unreadFetchedAt = Date.now()
    },
    // The list and the pop-up carry the community's timezone too, so no
    // screen waits for the bell to show times in it.
    setTimezone(state, timezone) {
      if (timezone) state.unread = { ...state.unread, timezone }
    },
    setLatest(state, { items, status }) {
      state.latest = withLocalCounts(items, state.localCounts, state.handled)
      state.latestLoaded = true
      state.latestStatus = status
    },
    setLatestLoading(state, value) {
      state.latestLoading = value
    },
    setLatestError(state, value) {
      state.latestError = value
    },
    setItemRead(state, { id, isRead }) {
      state.latest = state.latest.map(item => (item.id === id ? { ...item, isRead, newSinceRead: isRead ? 0 : item.newSinceRead } : item))
    },
    setAllRead(state, category) {
      state.latest = state.latest.map(item => (!category || item.category === category ? { ...item, isRead: true, newSinceRead: 0 } : item))
    },
    // Optimistic "mark all read": the badge clears at once, and comes back
    // from the snapshot if the server refuses.
    clearUnread(state, category) {
      if (!category) {
        state.unread = { ...state.unread, total: 0, byCategory: {} }
        return
      }
      const removed = state.unread.byCategory[category] || 0
      state.unread = {
        ...state.unread,
        total: Math.max(0, state.unread.total - removed),
        byCategory: { ...state.unread.byCategory, [category]: 0 },
      }
    },
    setRechecked(state) {
      state.recheckedAt = Date.now()
    },
    setLocalCount(state, { id, pendingCount }) {
      const now = Date.now()
      const kept = Object.fromEntries(Object.entries(state.localCounts).filter(([, local]) => local.until >= now))
      state.localCounts = { ...kept, [id]: { pendingCount, until: now + LOCAL_COUNT_MS } }
      state.latest = withLocalCounts(state.latest, state.localCounts, state.handled)
    },
    addHandled(state, handled) {
      const now = Date.now()
      state.handled = [...state.handled.filter(done => done.until >= now), { ...handled, until: now + LOCAL_COUNT_MS }]
      state.latest = withLocalCounts(state.latest, state.localCounts, state.handled)
    },
    restore(state, snapshot) {
      state.unread = snapshot.unread
      state.latest = snapshot.latest
    },
    reset(state) {
      state.unread = emptyUnread()
      state.unreadFetchedAt = 0
      state.latest = []
      state.latestLoaded = false
      state.latestLoading = false
      state.latestError = false
      state.latestStatus = 'all'
      state.localCounts = {}
      state.handled = []
      state.recheckedAt = 0
    },
  },
  actions: {
    fetchUnread({ commit, state }, { force = false } = {}) {
      if (!localStorage.getItem('token')) return Promise.resolve()
      if (!force && Date.now() - state.unreadFetchedAt < UNREAD_THROTTLE_MS) return Promise.resolve()
      if (unreadRequest) return unreadRequest
      unreadRequest = getUnreadNotificationCount()
        .then(res => {
          // A failed refresh keeps the last value, silently.
          if (res?.statusCode === 200) commit('setUnread', res.data)
        })
        .finally(() => {
          unreadRequest = null
        })
      return unreadRequest
    },
    async fetchLatest({ commit }, { status = 'all' } = {}) {
      commit('setLatestLoading', true)
      commit('setLatestError', false)
      try {
        const res = await getNotifications({ limit: LATEST_LIMIT, offset: 0, status })
        if (res?.statusCode === 200) {
          commit('setTimezone', res.meta?.timezone)
          commit('setLatest', { items: res.data || [], status })
        } else {
          commit('setLatestError', true)
        }
      } catch (e) {
        commit('setLatestError', true)
      } finally {
        commit('setLatestLoading', false)
      }
    },
    // Optimistic; rolled back if the server refuses.
    async markRead({ commit, dispatch }, item) {
      if (!item || item.isRead) return true
      commit('setItemRead', { id: item.id, isRead: true })
      const res = await markNotificationRead(item.id)
      if (res?.statusCode !== 200) {
        commit('setItemRead', { id: item.id, isRead: false })
        return false
      }
      dispatch('fetchUnread', { force: true })
      return true
    },
    async markAllRead({ commit, dispatch, state }, category) {
      const snapshot = { unread: state.unread, latest: state.latest }
      commit('setAllRead', category)
      commit('clearUnread', category)
      const res = await markAllNotificationsRead(category)
      if (res?.statusCode !== 200) {
        commit('restore', snapshot)
        return false
      }
      dispatch('fetchUnread', { force: true })
      return true
    },
    // The bell's "Notifications are paused · Turn on".
    async resume({ dispatch }) {
      const res = await updateNotificationSettings({ paused: false })
      if (res?.statusCode !== 200) return false
      dispatch('fetchUnread', { force: true })
      return true
    },
    // Join requests were approved / rejected: in the people pop-up (`item` is
    // the notification with its new pending count) or on a requests page (no
    // item, or `handled`: the request type, entity and user ids). The rows
    // update now; the badge and the bell follow the server.
    async requestsHandled({ commit, dispatch, state }, item) {
      if (item?.id) commit('setLocalCount', { id: item.id, pendingCount: Math.max(0, item.pendingCount || 0) })
      clearRechecks()
      recheckTimers = RECHECK_MS.map(delay => setTimeout(() => {
        commit('setRechecked')
        dispatch('fetchUnread', { force: true })
        if (state.latestLoaded) dispatch('fetchLatest', { status: state.latestStatus })
      }, delay))
      if (!item?.handled) return
      const handled = {
        type: item.handled.type,
        entity: item.handled.entity || {},
        userIds: (item.handled.userIds || []).map(String),
      }
      commit('addHandled', handled)
      // The server recounts no sooner than 5 s after an approve / reject, so
      // the pending rows read now still count these people, whether or not
      // the bell has them.
      const res = await getNotifications({ limit: PENDING_LIMIT, offset: 0, status: 'action' }).catch(() => null)
      const rows = res?.statusCode === 200 ? res.data || [] : state.latest
      const now = Date.now()
      handledPerRow(rows, handled).forEach((count, row) => {
        const local = state.localCounts[row.id]
        const left = local && local.until >= now ? Math.min(local.pendingCount, row.pendingCount) : row.pendingCount
        commit('setLocalCount', { id: row.id, pendingCount: Math.max(0, left - count) })
      })
    },
    reset({ commit }) {
      clearRechecks()
      commit('reset')
    },
  },
}
